import { bool, func, number, oneOfType, shape, string } from 'prop-types'
import { type FunctionComponent, useCallback, useEffect, useState } from 'react'
import { connectToggleRefinement } from 'react-instantsearch-dom'

import { FILTER_FLYOUT_CLOSE } from 'shared/experiments/components/NewSerp/consts'
import {
  type RefinementListCloseAction,
  type ShippingOptionResetAction,
  type ShippingOptionToggleAction,
} from 'shared/experiments/components/NewSerp/types'
import store, { subscribe } from 'shared/store'
import { type InferPropsWithDefaults } from 'shared/utils/propTypes'
import { upperCaseFirst } from 'shared/utils/stringUtils'
import InputCheckbox from 'views/components/molecules/InputCheckbox'

import {
  SHIPPING_OPTIONS_RESET_EVENT,
  SHIPPING_OPTIONS_TOGGLE_EVENT,
} from './consts'

const ShippingToggleOption: FunctionComponent<ComponentProps> = ({
  attribute,
  count,
  currentRefinement,
  id,
  label,
  nowLabel,
  refine,
}) => {
  const [isChecked, setChecked] = useState(currentRefinement)

  useEffect(
    () =>
      subscribe.after(
        SHIPPING_OPTIONS_RESET_EVENT,
        (payload: ShippingOptionResetAction) => {
          if (attribute === payload.attribute) {
            // rAF to fix the race conditions
            requestAnimationFrame(() => refine(''))
            setChecked(false)
          }
        }
      ),
    [attribute, refine]
  )

  useEffect(
    () =>
      subscribe.after(
        FILTER_FLYOUT_CLOSE,
        (payload: RefinementListCloseAction) => {
          if (payload.attribute === 'shippingOptions') {
            store.dispatch({
              type: SHIPPING_OPTIONS_TOGGLE_EVENT,
              value: currentRefinement,
              refine,
              attribute,
            } as ShippingOptionToggleAction)

            setChecked(currentRefinement)
          }
        }
      ),
    [attribute, currentRefinement, refine]
  )

  const handleChange = useCallback(() => {
    store.dispatch({
      type: SHIPPING_OPTIONS_TOGGLE_EVENT,
      value: !isChecked,
      refine,
      attribute,
    } as ShippingOptionToggleAction)

    setChecked(!isChecked)
  }, [attribute, isChecked, refine])

  return count?.unchecked ? (
    <li className="u-no-border-radius u-border--bottom@small-tiny u-padding@small-tiny u-margin-x-small--bottom@medium">
      <InputCheckbox
        id={`${id}-checkbox`}
        name={`${id}-checkbox`}
        checked={Boolean(isChecked)}
        // @ts-ignore
        onChange={handleChange}
        disableAutoCompleteAndFriends
        nowLabel={nowLabel}
        labelSize="small"
      >
        {upperCaseFirst(label)}
        {Boolean(count.unchecked) && (
          <span suppressHydrationWarning> ({count.unchecked})</span>
        )}
      </InputCheckbox>
    </li>
  ) : null
}

const propTypes = {
  attribute: string.isRequired,
  id: string.isRequired,
  label: string.isRequired,
  refine: func.isRequired,
  currentRefinement: oneOfType([bool, string]).isRequired,
  count: shape({ unchecked: number, checked: number }).isRequired,
  nowLabel: bool,
}

const defaultProps = {
  nowLabel: false,
}

type ComponentProps = InferPropsWithDefaults<
  typeof propTypes,
  typeof defaultProps
>

export default connectToggleRefinement(ShippingToggleOption)
